import { Layout } from 'antd';
import type { Location } from 'history';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { SDetailLayout } from 'app/components/StaycationUI';
import { PackDetailTitle } from 'app/components/commons/AssetDetailTitle/PackDetailTitle';
import {
  useGetPackage,
  useGetPackageCopywriting,
  useGetPackageDisclaimer,
} from 'app/hooks/data/packages/usePackages';
import { useSavePackageDisclaimer } from 'app/hooks/data/packages/useSavePackage';
import type { PackageDisclaimer } from 'app/typings/packages';
import { DisclaimerKind } from 'app/typings/packages';

import { DisclaimerCategory } from './components/DisclaimerCategory/DisclaimerCategory';

const { Content } = Layout;

type Mode = 'view' | 'edit';

type Props = {
  headerMenu: ReactNode;
  headerExtra: ReactNode[];
  packageId: number | null;
};

const noop = () => {
  /* no-op */
};

export const Disclaimer = ({ headerMenu, headerExtra, packageId }: Props) => {
  const { data: packageCopywriting } = useGetPackageCopywriting(packageId);
  const { data: pkg } = useGetPackage(packageId);
  const { mutateAsync: saveDisclaimers } = useSavePackageDisclaimer();
  const { state } = useLocation<Location>();

  const { data: disclaimers, isLoading: disclaimersLoading } =
    useGetPackageDisclaimer(packageId);
  const [disclaimersValues, setDisclaimersValues] = useState<
    PackageDisclaimer[]
  >([]);

  useEffect(() => {
    if (!disclaimersLoading && disclaimers) {
      setDisclaimersValues(disclaimers);
    }
  }, [disclaimersLoading, disclaimers]);

  const disclaimersAdvice = useMemo(
    () =>
      disclaimersValues
        .filter((d) => d.kind === DisclaimerKind.ADVICE)
        .sort((a, b) => a.priority - b.priority),
    [disclaimersValues]
  );
  const disclaimersAlert = useMemo(
    () =>
      disclaimersValues
        .filter((d) => d.kind === DisclaimerKind.ALERT)
        .sort((a, b) => a.priority - b.priority),
    [disclaimersValues]
  );

  const [mode, setMode] = useState<Mode>('view');
  const [isDirty, setIsDirty] = useState(false);

  const onEdit = useCallback(() => {
    setMode('edit');
  }, []);

  const onCancel = useCallback(() => {
    setIsDirty(false);
    setMode('view');
  }, []);

  const onChange = useCallback(
    (disclaimersChanged: PackageDisclaimer[], kind: DisclaimerKind) => {
      const newDisclaimersAdvice =
        kind === DisclaimerKind.ADVICE ? disclaimersChanged : disclaimersAdvice;
      const newDisclaimersAlert =
        kind === DisclaimerKind.ALERT ? disclaimersChanged : disclaimersAlert;

      const newDisclaimers = newDisclaimersAdvice.concat(newDisclaimersAlert);

      const isDisclaimersValid = newDisclaimers.every(
        (d) => d.text.length && d.startDate
      );

      setDisclaimersValues(newDisclaimers);
      setIsDirty(isDisclaimersValid);
    },
    [disclaimersAdvice, disclaimersAlert]
  );

  const onSave = useCallback(() => {
    if (packageId) {
      saveDisclaimers({ packageId, payload: disclaimersValues });
    }

    setIsDirty(false);
    setMode('view');
  }, [packageId, saveDisclaimers, disclaimersValues]);

  useEffect(() => {
    if (mode === 'view' && disclaimers) {
      // We reset the page with the value stored in the state when the mode is changed to view
      setDisclaimersValues(disclaimers);
    }
  }, [mode, disclaimers]);

  if (!packageId) {
    return null;
  }

  return (
    <SDetailLayout
      title={
        <PackDetailTitle
          packageId={packageId}
          hotelId={pkg?.hotelId}
          aliasForHotel={packageCopywriting?.aliasForHotel}
        />
      }
      mode={mode}
      onEdit={onEdit}
      onSave={onSave}
      onCancel={onCancel}
      reset={noop}
      isDirty={isDirty}
      headerMenu={headerMenu}
      headerExtra={headerExtra}
      isValid
      backRoute={state?.pathname}
    >
      <Content className="package-detail__content">
        <DisclaimerCategory
          mode={mode}
          kind={DisclaimerKind.ADVICE}
          onValueChanged={onChange}
          disclaimers={disclaimersAdvice}
          packageId={packageId}
        />
        <DisclaimerCategory
          mode={mode}
          kind={DisclaimerKind.ALERT}
          onValueChanged={onChange}
          disclaimers={disclaimersAlert}
          packageId={packageId}
        />
      </Content>
    </SDetailLayout>
  );
};
